import React from "react";
import {PropTypes} from "prop-types";
import MaskedInput from "react-input-mask";

// set up the mask chars.
export const MASK_FORMAT_CHAR = "_";
export const MASK_FORMAT_HANDLERS = {
    // basic masks
    "#": { // Number
        reg: "[0-9]",
    },
    "a": { // Character
        reg: "[A-Za-z]",
    },
    "*": { // Alpha Numeric
        reg: "[A-Za-z0-9]",
    },

    // date time masks
    "M": { // Month
        reg: "[0-9]",
        validate: (chunk) => {
            if(chunk.length === 2) {
                chunk.value = paddedIntegerNumber(chunk.value, "01", "12", chunk.placeholder);
            }
            return chunk;
        }
    },
    "D": { // Day
        reg: "[0-9]",
        validate: (chunk, chunks) => {
            if(chunk.length === 2) {
                let maxDays = "31"; // default to 31 days.
                const monthChunk = chunks.find((c) => c.mask === "M");
                // check if month mask comes before day mask
                if(monthChunk && monthChunk.index < chunk.index) {
                    // get the number of days in the month
                    switch(monthChunk.value) {
                        case "02": // february, 28/29
                            maxDays = "29";
                            break;
                        case "04": case "06": case "09": case "11": // may, june, september, november, 30
                            maxDays = "30";
                            break;
                        default:
                            maxDays = "31";
                    }
                }
                chunk.value = paddedIntegerNumber(chunk.value, "01", maxDays, chunk.placeholder);
            }
            return chunk;
        }
    },
    "Y": { // Year
        reg: "[0-9]",
        validate: (chunk) => {
            if(chunk.length === 4) {
                if(["18","19","20"].includes(chunk.value.substr(0,2))) {
                    // valid centuries
                    chunk.value = paddedIntegerNumber(chunk.value, "1850", "2099", chunk.placeholder);
                }
                else {
                    // partial year
                    chunk.value = paddedIntegerNumber(chunk.value.substr(0,2), "00", "99", chunk.placeholder) + chunk.placeholder + chunk.placeholder;
                }
            }
            return chunk;
        }
    },
    "H": { // 24 Hour
        reg: "[0-9]",
        validate: (chunk) => {
            if(chunk.length === 2) {
                chunk.value = paddedIntegerNumber(chunk.value, "00", "23", chunk.placeholder);
            }
            return chunk;
        }
    },
    "h": { // 12 Hour
        reg: "[0-9]",
        validate: (chunk) => {
            if(chunk.length === 2) {
                chunk.value = paddedIntegerNumber(chunk.value, "01", "12", chunk.placeholder);
            }
            return chunk;
        }
    },
    "m": { // Minute
        reg: "[0-9]",
        validate: (chunk) => {
            if(chunk.length === 2) {
                chunk.value = paddedIntegerNumber(chunk.value, "00", "59", chunk.placeholder);
            }
            return chunk;
        }
    },
    "s": { // Second
        reg: "[0-9]",
        validate: (chunk) => {
            if(chunk.length === 2) {
                chunk.value = paddedIntegerNumber(chunk.value, "00", "59", chunk.placeholder);
            }
            return chunk;
        }
    }
};

const MASK_FORMAT_CHARS = {};
const MASK_FORMAT_KEYS = Object.keys(MASK_FORMAT_HANDLERS);
MASK_FORMAT_KEYS.forEach((v) => MASK_FORMAT_CHARS[v] = MASK_FORMAT_HANDLERS[v].reg);

export default class InputMask extends React.Component {
    constructor(props) {
        super(props);
        this.inputRef = props._ref || React.createRef();

        if(props.maskFormat) {
            this.onMouseEnter = this.onMouseEnter.bind(this);
            this.onMouseLeave = this.onMouseLeave.bind(this);
            this.onBlur = this.onBlur.bind(this);
            this.onChange = this.onChange.bind(this);
            this.onBeforeMaskedValueChange = this.onBeforeMaskedValueChange.bind(this);
            const maskFormat = props.maskFormat;
            if (maskFormat.mask && maskFormat.placeholder && maskFormat.mask.length !== maskFormat.placeholder.length) {
                // The placeholder characters can be different from mask but the
                // placeholder.length needs to be the same as mask.length
                // this is so we can look up the correct placeholder based caret
                // location.
                console.error("InputMask: maskFormat.placeholder and maskFormat.mask must be same length.");
            }
        }
    }

    onMouseEnter(e) {
        const { onMouseEnter, maskFormat, disabled } = this.props;
        (!disabled && maskFormat && maskFormat.placeholder) && this.inputRef.current.setAttribute("placeholder", maskFormat.placeholder);
        onMouseEnter && onMouseEnter(e);
    }

    onMouseLeave(e) {
        const { onMouseLeave, maskFormat} = this.props;
        maskFormat && this.inputRef.current.setAttribute("placeholder", this.props.placeholder);
        onMouseLeave && onMouseLeave(e);
    }

    onBlur(e) {
        const { onBlur } = this.props;
        onBlur && onBlur(e);
    }

    onChange(e) {
        const {onChange} = this.props;
        onChange && onChange(e);
    }

    replaceMaskWithLetter(value = "", letter) {
        return value.replace(new RegExp(`[_${letter}]`,"g"), letter);
    }

    fillInMaskWithPlaceholderLetters(value) {
        const {maskFormat} = this.props;
        if (!value) return "";
        // make chunks
        const chunks = [];
        let chunkValue = "";
        let chunkLength = 0;
        value.split("").forEach((c, index) => {
            const m = maskFormat.mask.charAt(index);
            if(!MASK_FORMAT_KEYS.includes(m) || index === maskFormat.mask.length-1) {
                if(index === maskFormat.mask.length-1) {
                    chunkLength++;
                    chunkValue += c;
                }
                chunks.push({
                    index: chunks.length,
                    length: chunkLength,
                    value: chunkValue,
                    placeholder: maskFormat.placeholder ? (maskFormat.placeholder.charAt(index-1) || MASK_FORMAT_CHAR) : MASK_FORMAT_CHAR,
                    mask: maskFormat.mask.charAt(index-1),
                    join: index === maskFormat.mask.length-1 ? "" : m
                });
                chunkValue = "";
                chunkLength = 0;
            }
            else {
                chunkLength++;
                chunkValue += c;
            }
        });
        // put chunks back together and run format function if defined.
        return chunks.map(chunk => {
            if(MASK_FORMAT_HANDLERS[chunk.mask] && typeof MASK_FORMAT_HANDLERS[chunk.mask].validate === "function") {
                chunk = MASK_FORMAT_HANDLERS[chunk.mask].validate(chunk, chunks, value);
            }
            return this.replaceMaskWithLetter(chunk.value, chunk.placeholder) + chunk.join;
        } ).join("");
    }

    findFirstPlaceholderIndex(value) {
        const {maskFormat} = this.props;
        for(let index = 0; index < value.length; index++) {
            const c = value.charAt(index);
            if((c === MASK_FORMAT_CHAR)) {
                return index;
            }
            if((maskFormat.placeholder && c === maskFormat.placeholder.charAt(index))) {
                if(MASK_FORMAT_KEYS.includes(maskFormat.mask.charAt(index))) {
                    return index;
                }
            }
        }
        return null;
    }

    onBeforeMaskedValueChange(maskState) {
        const {maskFormat} = this.props;
        const newMaskState = {
            value: this.fillInMaskWithPlaceholderLetters(maskState.value),
            selection: maskState.selection ? {...maskState.selection} : null
        };

        const index = this.findFirstPlaceholderIndex(newMaskState.value) || newMaskState.value.length-1;
        if (newMaskState.selection && index !== newMaskState.value.length-1) {
            // reset placeholder values after first found placeholder character.
            // Replaces 01/0d/2000 with 01/0d/yyyy
            // This only happens when user copy & pasts a improperly formatted
            // string or hits backspace in the middle of the input mask.
            newMaskState.value = newMaskState.value.substr(0,index) + maskFormat.placeholder.substr(index);
            // update the cursor position
            newMaskState.selection = {
                start: index,
                end: index
            };
        }
        return newMaskState;
    }

    render() {
        const { maskFormat, placeholder, value } = this.props;
        const cleanProps = {...this.props};
        delete cleanProps._ref;
        delete cleanProps.inputRef;
        delete cleanProps.maskFormat;
        delete cleanProps.maxLength;
        if(!maskFormat) {
            // nothing needs to be overloaded. Pass through all props to the input and be done.
            return (<input
                {...cleanProps}
                ref={this.inputRef}
            />);
        }
        // wire up all the maskFormat event wrappers.
        return (<MaskedInput
            {...cleanProps}
            type="input"
            inputRef={(ref) => this.inputRef.current = ref}
            mask={maskFormat.mask}
            maskChar={MASK_FORMAT_CHAR}
            formatChars={MASK_FORMAT_CHARS}
            alwaysShowMask={false}
            beforeMaskedValueChange={this.onBeforeMaskedValueChange}
            onChange={this.onChange}
            onBlur={this.onBlur}
            onMouseEnter={this.onMouseEnter}
            onMouseLeave={this.onMouseLeave}
            value={value}
            placeholder={placeholder}
        />);
    }
}

InputMask.propTypes = {
    maskFormat: PropTypes.shape({
        mask: PropTypes.string.isRequired,
        placeholder: PropTypes.string,
        momentjs: PropTypes.string,
    }),
    value: PropTypes.string
};


// format helper functions
export const paddedIntegerNumber = (value, min, max, placeholder = MASK_FORMAT_CHAR) => {
    if(value.indexOf(placeholder) === 0) {
        // nothing typed
        return value;
    }
    // split numbers into arrays with padding
    const minInts = min.split("").map(c => parseInt(c, 10));
    const maxInts = max.split("").map(c => parseInt(c, 10));
    let valueInts = value.split("").map(c => parseInt(c, 10));
    const valueChars = value.split("");

    if(max.length === 2 && valueInts[0] > maxInts[0] ) {
        // add leading zero
        valueInts[1] = valueInts[0];
        valueInts[0] = 0;
    }

    // prevent input if the provided value is going to be greater or less then
    // min and max.
    let checkMax = true;
    let checkMin = true;
    let clearRemaining = false;
    valueInts = valueInts.map((v, index) => {
        if(isNaN(v) || clearRemaining){
            return NaN;
        }
        if(checkMax && ( v > maxInts[index])) {
            clearRemaining = true;
            return NaN; // the current index value is out of range set to NaN
        }
        if(checkMin && ( v < minInts[index])) {
            clearRemaining = true;
            return NaN; // the current index value is out of range set to NaN
        }
        checkMax = v === maxInts[index];
        checkMin = v === minInts[index];
        return v;
    });
    // put it back together
    return valueChars.map((c, i) => !isNaN(valueInts[i]) ? valueInts[i].toString() : placeholder).join("");
};



// WEBPACK FOOTER //
// ./app/lib/common/descriptorcontrols/BaseControls/InputMask/InputMask.js